import { useState } from "react";

import { useT } from "../../shared/i18n";
import { Banner, Button, Dialog } from "../../shared/ui";
import type { BackfillLine } from "./changes";

/** Publishing will fill existing records with a default (ADR 0036, 0039). */
export function BackfillNotice({ lines }: { lines: readonly BackfillLine[] }) {
  const t = useT();
  if (lines.length === 0) return null;
  return (
    <Banner tone="info" title={t("draft.backfill.title")}>
      <span className="block">
        {lines.map((line) => (
          <span key={line.field} className="block">
            {t("draft.backfill.field")} «{line.field}»: {line.records}{" "}
            {t("draft.backfill.records")}, {t("draft.backfill.value")} «
            {line.value}»
          </span>
        ))}
      </span>
    </Banner>
  );
}

/**
 * The Draft was cut from a version that is no longer active (ADR 0199). It
 * cannot be published; the only way out is to delete it and ask again.
 */
export function StaleDraftBanner({
  discard,
}: {
  /** Delete the Draft; rejects when it could not be deleted. */
  discard: () => Promise<void>;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [failed, setFailed] = useState(false);
  const confirm = () => {
    setRunning(true);
    setFailed(false);
    discard()
      .then(() => {
        setOpen(false);
      })
      .catch(() => {
        setFailed(true);
      })
      .finally(() => {
        setRunning(false);
      });
  };
  return (
    <>
      <Banner
        tone="warning"
        title={t("draft.staleBase.title")}
        action={
          <Button
            size="sm"
            variant="tinted"
            onClick={() => {
              setOpen(true);
            }}
          >
            {t("draft.staleBase.discard")}
          </Button>
        }
      >
        {t("draft.staleBase.body")}
      </Banner>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!running) setOpen(next);
        }}
        title={t("draft.interface.confirm.title")}
        description={
          failed
            ? t("draft.interface.failed")
            : t("draft.interface.confirm.body")
        }
        footer={
          <>
            <Button
              variant="secondary"
              disabled={running}
              onClick={() => {
                setOpen(false);
              }}
            >
              {t("record.cancel")}
            </Button>
            <Button loading={running} onClick={confirm}>
              {t("draft.staleBase.discard")}
            </Button>
          </>
        }
      />
    </>
  );
}
